import { Button, IconDocument, useAction, useHelmet, useHistoryPush } from '@agro-club/frontend-shared'
import useDownload from 'hooks/useDownload'
import AuthSelectors from 'modules/domain/auth/selectors'
import { useTargetSkuOrderUpdate } from 'modules/domain/target2sku/common/hooks'
import { TargetSkuTypes } from 'modules/domain/target2sku/common/types'
import FarmerTargetSkuSelectors from 'modules/domain/target2sku/farmerTarget/selectors'
import { RetailerTargetSkuSelectors } from 'modules/domain/target2sku/retailerTarget/selectors'
import { CropTargetSkuSelectors } from 'modules/domain/target2sku/cropTarget/selectors'
import { PedigreedTargetSkuSelectors } from 'modules/domain/target2sku/pedigreedTarget/selectors'
import { PedigreedGrowerTargetSkuSelectors } from 'modules/domain/target2sku/pedigreedGrowerTarget/selectors'
import { TerritoryTargetSkuSelectors } from 'modules/domain/target2sku/territoryTarget/selectors'
import { CAPABILITY, PERMISSION, usePermissions } from 'modules/permissions/permissions'
import { octopusApiClient } from 'modules/utils/httpClient'
import React, { useMemo, useState } from 'react'
import AnimateHeight from 'react-animate-height'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { isAdmin, isAgro, isDistributor } from 'types/entities'
import * as TComponents from 'views/components/CommonTableComponents/CommonTableComponents'
import * as Layout from 'views/layouts/MainLayout/MainLayout'
import * as Header from 'views/ui/Header/Header'
import { TargetSkuFarmerSelect } from './components/selects/TargetSkuFarmerSelect'
import { TargetSkuGrowerSelect } from './components/selects/TargetSkuGrowerSelect'
import { TargetSkuManufacturerSelect } from './components/selects/TargetSkuManufacturerSelect'
import { TargetSkuProductSelect } from './components/selects/TargetSkuProductSelect'
import { TargetSkuRetailerSelect } from './components/selects/TargetSkuRetailerSelect'
import { TargetSkuSeasonSelect } from './components/selects/TargetSkuSeasonSelect'
import { TargetSkuSubcategorySelect } from './components/selects/TargetSkuSubcategorySelect'
import { TargetSkuTerritorySelect } from './components/selects/TargetSkuTerritorySelect'
import FarmerTargetSkuTable from './components/tables/FarmerTargetSkuTable'
import RetailerTargetSkuTable from './components/tables/RetailerTargetSkuTable'
import CropTargetSkuTable from './components/tables/CropTargetSkuTable'
import PedigreedTargetSkuTable from './components/tables/PedigreedTargetSkuTable'
import PedigreedGrowerTargetSkuTable from './components/tables/PedigreedGrowerTargetSkuTable'
import { TerritoryTargetSkuTable } from './components/tables/TerritoryTargetSkuTable'
import { mappedActions, mappedDownloadCSVEndpoint, mappedSelectors } from './constants'
import Target2SkuRoutes from './routes'

const FilterWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, 220px);
  grid-gap: 16px;
  padding: 24px 36px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
`

const StyledButton = styled(Button)`
  margin-left: 8px;
`
const SecondaryActionsButtons = styled.div`
  position: absolute;
  bottom: 10px;
  right: 24px;
`

const TargetSkuList: React.FC<{ targetType: TargetSkuTypes }> = ({ targetType }) => {
  const { t } = useTranslation('target2')

  const push = useHistoryPush()

  const actions = mappedActions[targetType]
  const selectors = mappedSelectors[targetType]

  const listUpdateRequested = useAction(actions.listUpdateRequested)

  const filterUpdated = useAction(actions.filterUpdated)
  const territoryFilterValue = useSelector(TerritoryTargetSkuSelectors.filter)
  const farmerFilterValue = useSelector(FarmerTargetSkuSelectors.filter)
  const retailerFilterValue = useSelector(RetailerTargetSkuSelectors.filter)
  const cropFilterValue = useSelector(CropTargetSkuSelectors.filter)
  const pedigreedFilterValue = useSelector(PedigreedTargetSkuSelectors.filter)
  const pedigreedGrowerFilterValue = useSelector(PedigreedGrowerTargetSkuSelectors.filter)
  const updateCounter = useSelector(selectors.updateCounter)

  const role = useSelector(AuthSelectors.role)
  const agro = isAgro(role)
  const admin = isAdmin(role)
  const distributor = isDistributor(role)

  const filterValue = useMemo(() => {
    switch (targetType) {
      case TargetSkuTypes.Territory:
        return territoryFilterValue
      case TargetSkuTypes.Retailer:
        return retailerFilterValue
      case TargetSkuTypes.Crop:
        return cropFilterValue
      case TargetSkuTypes.Farmer:
        return farmerFilterValue
      case TargetSkuTypes.Pedigreed:
        return pedigreedFilterValue
      case TargetSkuTypes.PedigreedGrower:
        return pedigreedGrowerFilterValue
      default:
        return {}
    }
  }, [
    cropFilterValue,
    farmerFilterValue,
    pedigreedFilterValue,
    pedigreedGrowerFilterValue,
    retailerFilterValue,
    targetType,
    territoryFilterValue,
  ])

  useHelmet({ title: `${t('metaTitle')}` })

  const table = useMemo(() => {
    switch (targetType) {
      case TargetSkuTypes.Territory:
        return <TerritoryTargetSkuTable />
      case TargetSkuTypes.Farmer:
        return <FarmerTargetSkuTable />
      case TargetSkuTypes.Retailer:
        return <RetailerTargetSkuTable />
      case TargetSkuTypes.Crop:
        return <CropTargetSkuTable />
      case TargetSkuTypes.Pedigreed:
        return <PedigreedTargetSkuTable />
      case TargetSkuTypes.PedigreedGrower:
        return <PedigreedGrowerTargetSkuTable />
    }
  }, [targetType])

  const [showTerritoryTab, showRetailerTab, showCropTab, showPedigreedTab, showPedigreedGrowerTab] = usePermissions([
    { capability: CAPABILITY.TERRITORY_TARGETS, permission: PERMISSION.R },
    { capability: CAPABILITY.RETAILER_TARGETS, permission: PERMISSION.R },
    { capability: CAPABILITY.CROP_TARGETS, permission: PERMISSION.R },
    { capability: CAPABILITY.PEDIGREED_TARGETS, permission: PERMISSION.R },
    { capability: CAPABILITY.PEDIGREED_GROWER_TARGETS, permission: PERMISSION.R },
  ])

  const [isFiltersVisible, setIsFiltersVisible] = useState(true)

  const [orderUpdateProgress, requestOrderUpdate] = useTargetSkuOrderUpdate(listUpdateRequested)

  const handleFilterUpdate = (newFilter: Partial<typeof filterValue>) =>
    filterUpdated({
      ...filterValue,
      ...newFilter,
    })

  const handlePersistentFilterToggle = () => {
    const newState = !isFiltersVisible
    setIsFiltersVisible(newState)
  }

  const downloadEndpoint = mappedDownloadCSVEndpoint[targetType]

  const [downloadProgress, downloadCSV] = useDownload(downloadEndpoint, filterValue, undefined, octopusApiClient)

  const showFiltersButton = false

  return (
    <>
      <Layout.Header>
        <Header.RootWithSmallTabs>
          <Header.Title title={`${t('headerTitle')}`}>
            {showFiltersButton && (
              <Button onClick={handlePersistentFilterToggle} intent={'primary'} size={'small'} filled>
                {t(isFiltersVisible ? 'list.filters.hide' : 'list.filters.show')}
              </Button>
            )}
            {admin && (
              <StyledButton
                progress={orderUpdateProgress}
                onClick={requestOrderUpdate}
                intent={'primary'}
                size={'small'}
                filled
              >
                {t('list.refreshOrders')}
              </StyledButton>
            )}
          </Header.Title>

          {(showRetailerTab || showTerritoryTab || showCropTab || showPedigreedTab || showPedigreedGrowerTab) && (
            <Header.SmallTabsContainer>
              {showTerritoryTab && (
                <Header.SmallTab
                  title={t('list.tabs.territory')}
                  isActive={targetType === TargetSkuTypes.Territory}
                  onClick={() => push({ route: Target2SkuRoutes.TerritoryList })}
                >
                  {t('list.tabs.territory')}
                </Header.SmallTab>
              )}
              {showRetailerTab && (
                <Header.SmallTab
                  title={t('list.tabs.retailer')}
                  isActive={targetType === TargetSkuTypes.Retailer}
                  onClick={() => push({ route: Target2SkuRoutes.RetailerList })}
                >
                  {t('list.tabs.retailer')}
                </Header.SmallTab>
              )}
              <Header.SmallTab
                title={t('list.tabs.farmer')}
                isActive={targetType === TargetSkuTypes.Farmer}
                onClick={() => push({ route: Target2SkuRoutes.FarmerList })}
              >
                {t('list.tabs.farmer')}
              </Header.SmallTab>
              {showCropTab && (
                <Header.SmallTab
                  title={t('list.tabs.crop')}
                  isActive={targetType === TargetSkuTypes.Crop}
                  onClick={() => push({ route: Target2SkuRoutes.CropList })}
                >
                  {t('list.tabs.crop')}
                </Header.SmallTab>
              )}
              {showPedigreedTab && (
                <Header.SmallTab
                  title={t('list.tabs.pedigreed')}
                  isActive={targetType === TargetSkuTypes.Pedigreed}
                  onClick={() => push({ route: Target2SkuRoutes.PedigreedList })}
                >
                  {t('list.tabs.pedigreed')}
                </Header.SmallTab>
              )}
              {showPedigreedGrowerTab && (
                <Header.SmallTab
                  title={t('list.tabs.pedigreedGrower')}
                  isActive={targetType === TargetSkuTypes.PedigreedGrower}
                  onClick={() => push({ route: Target2SkuRoutes.PedigreedGrowerList })}
                >
                  {t('list.tabs.pedigreedGrower')}
                </Header.SmallTab>
              )}
            </Header.SmallTabsContainer>
          )}
          <SecondaryActionsButtons>
            <Button progress={downloadProgress} intent="primary" size="small" variant="text" onClick={downloadCSV}>
              <IconDocument />
              {t('labels:downloadCsv')}
            </Button>
          </SecondaryActionsButtons>
        </Header.RootWithSmallTabs>
        <AnimateHeight height={isFiltersVisible ? 'auto' : 0} duration={400} animateOpacity>
          <FilterWrapper>
            {agro && (
              <TargetSkuManufacturerSelect
                label={t('list.filters.labels.manufacturer')}
                placeholder={t('list.filters.placeholders.manufacturer')}
                onChange={(val) => {
                  handleFilterUpdate({
                    manufacturer_id: val,
                    territory_id: undefined,
                    retailer_id: undefined,
                    farmer_id: undefined,
                    grower_id: undefined,
                    season_id: undefined,
                    product_id: undefined,
                    subcategory_id: undefined,
                  })
                }}
                value={filterValue.manufacturer_id}
                variant="small"
                isClearable
                scope={targetType}
                updateDeps={[updateCounter, targetType]}
              />
            )}
            <TargetSkuTerritorySelect
              label={t('list.filters.labels.territory')}
              placeholder={t('list.filters.placeholders.territory')}
              onChange={(val) => {
                handleFilterUpdate({ territory_id: val, retailer_id: undefined, grower_id: undefined })
              }}
              value={filterValue.territory_id}
              variant="small"
              scope={targetType}
              manufacturerId={filterValue.manufacturer_id}
              updateDeps={[updateCounter, targetType]}
              isClearable
            />
            {targetType !== TargetSkuTypes.Territory &&
              targetType !== TargetSkuTypes.Crop &&
              targetType !== TargetSkuTypes.Pedigreed &&
              targetType !== TargetSkuTypes.PedigreedGrower &&
              !distributor && (
                <TargetSkuRetailerSelect
                  label={t('list.filters.labels.retailer')}
                  placeholder={t('list.filters.placeholders.retailer')}
                  onChange={(val) => {
                    handleFilterUpdate({ retailer_id: val })
                  }}
                  value={filterValue.retailer_id}
                  variant="small"
                  scope={targetType}
                  manufacturerId={filterValue.manufacturer_id}
                  territoryId={filterValue.territory_id}
                  updateDeps={[updateCounter, targetType]}
                  isClearable
                />
              )}
            {targetType === TargetSkuTypes.PedigreedGrower && (
              <TargetSkuGrowerSelect
                label={t('list.filters.labels.grower')}
                placeholder={t('list.filters.placeholders.grower')}
                onChange={(val) => {
                  handleFilterUpdate({ grower_id: val })
                }}
                value={filterValue.grower_id}
                variant="small"
                scope={targetType}
                manufacturerId={filterValue.manufacturer_id}
                territoryId={filterValue.territory_id}
                updateDeps={[updateCounter, targetType]}
                isClearable
              />
            )}
            {targetType === TargetSkuTypes.Farmer && (
              <TargetSkuFarmerSelect
                label={t('list.filters.labels.farmer')}
                placeholder={t('list.filters.placeholders.farmer')}
                onChange={(val) => {
                  handleFilterUpdate({ farmer_id: val })
                }}
                value={filterValue.farmer_id}
                variant="small"
                scope={targetType}
                manufacturerId={filterValue.manufacturer_id}
                updateDeps={[updateCounter, targetType]}
                isClearable
              />
            )}
            <TargetSkuSeasonSelect
              label={t('list.filters.labels.seasons')}
              placeholder={t('list.filters.placeholders.seasons')}
              values={filterValue.season_id}
              onChange={(val) => {
                handleFilterUpdate({ season_id: val })
              }}
              variant="small"
              scope={targetType}
              manufacturerId={filterValue.manufacturer_id}
              updateDeps={[updateCounter, targetType]}
              isClearable
            />
            <TargetSkuSubcategorySelect
              label={t('list.filters.labels.subcategory')}
              placeholder={t('list.filters.placeholders.subcategory')}
              value={filterValue.subcategory_id}
              onChange={(val) => {
                handleFilterUpdate({ subcategory_id: val, product_id: undefined })
              }}
              variant="small"
              scope={targetType}
              manufacturerId={filterValue.manufacturer_id}
              updateDeps={[updateCounter, targetType]}
              isClearable
            />
            {targetType !== TargetSkuTypes.Crop && (
              <TargetSkuProductSelect
                label={t('list.filters.labels.product')}
                placeholder={t('list.filters.placeholders.product')}
                value={filterValue.product_id}
                onChange={(val) => {
                  handleFilterUpdate({ product_id: val })
                }}
                variant="small"
                scope={targetType}
                manufacturerId={filterValue.manufacturer_id}
                subcategoryId={filterValue.subcategory_id}
                updateDeps={[updateCounter, targetType]}
                isClearable
              />
            )}
          </FilterWrapper>
        </AnimateHeight>
      </Layout.Header>
      <Layout.Content>
        <TComponents.Wrapper noFilters>{table}</TComponents.Wrapper>
      </Layout.Content>
    </>
  )
}

export default TargetSkuList
